import React, { useState, useEffect } from "react";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import Switch from "../library/Switch";
import Skeleton from "../library/Skeleton";
import ErrorText from "../library/ErrorText";
import RecruiterUser from "../../apiResources/RecruiterUser";
import SettingLabel from "./SettingLabel";
import { useDidMountEffect } from "../../hooks";

const JobNotificationSettingOption = (props: Props) => {
  const { user, onUserChange, isLoading, disabled } = props;

  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<string>(null);

  const clearError = () => setError(null);

  const updateUser = async () => {
    clearError();

    try {
      user.assignAttributes({
        receiveJobNotificationEmails: enabled,
      });
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
      } else {
        setError(user.errors.receiveJobNotificationEmails.fullMessage);
      }
    } catch (err) {
      Sentry.captureException(err);
      setError(
        "Oops, something went wrong updating your notification settings"
      );
    }
  };

  useEffect(() => {
    // When an error is set it means that something went wrong while trying to
    // sync with the server, so we want to undo the change the user made to show
    // them that it hasn't updated.
    if (error) {
      setEnabled(!enabled);
    }
  }, [error]);

  useDidMountEffect(() => {
    if (!error) {
      void updateUser();
    }
  }, [enabled]);

  useEffect(() => {
    setEnabled(Boolean(user?.receiveJobNotificationEmails));
  }, [user]);

  return (
    <section>
      <Header>
        <SettingLabel>Notifications</SettingLabel>
        <span>Email</span>
      </Header>
      <Row>
        <SwitchLabel>
          Get notified when new jobs are added to the platform
        </SwitchLabel>
        {isLoading ? (
          <Skeleton height="16" />
        ) : (
          <Switch
            aria-label="Enable email notifications for new jobs"
            name="jobNotification"
            isChecked={enabled}
            onChange={(newValue) => {
              clearError();
              setEnabled(newValue);
            }}
            disabled={disabled}
          />
        )}
      </Row>
      {error && <ErrorText>{error}</ErrorText>}
    </section>
  );
};

type Props = {
  user: RecruiterUser;
  onUserChange?: (user: RecruiterUser) => void;
  isLoading?: boolean;
  disabled?: boolean;
};

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const Header = styled(Row)`
  margin-bottom: 1rem;
`;

const SwitchLabel = styled.div`
  flex: 1;
  margin-right: 1rem;
  margin-bottom: 0.5rem;
`;

export default JobNotificationSettingOption;
